import React, { useState, useEffect, useMemo } from 'react';
import {
  Settings, Wifi, Monitor, Phone, Headphones, Tablet, Smartphone,
  ChevronDown, ChevronUp, CheckCircle, AlertCircle, Edit2, FileText,
  PoundSterlingIcon, Globe, Zap, Calculator
} from 'lucide-react';
import { getSystemConfig } from '../services/systemConfigService';
import { getRentalProfiling } from '../services/rentalProfilingService';
import { fetchMechanicsData } from '../services/mechanicsService';
import MobileService from '../services/mobileService';

/* ────────────────────────────────────────────────────────────────────────
   Static reference data — mirrors the constants used in ExistingSystemConfig,
   CallCostsComponent and CostComparisonComponent so saved figures can be
   labelled/derived correctly without needing those components' live state.
   ──────────────────────────────────────────────────────────────────────── */

const getDefaultHardwareItems = () => [
  { id: 1, name: 'Bell / Tannoy', category: 'Audio', icon: '/images/bell.jpg' },
  { id: 2, name: 'Mobile App', category: 'Software', icon: '/images/mobileapp.jpg' },
  { id: 3, name: 'Mobile App Addon', category: 'Software', icon: '📱' },
  { id: 4, name: 'T54W', category: 'Phone', icon: '/images/t54w.png' },
  { id: 5, name: 'T57W', category: 'Phone', icon: '/images/t57w.jpg' },
  { id: 6, name: 'T58W', category: 'Phone', icon: '/images/t58w.jpg' },
  { id: 7, name: 'YEALINK T85', category: 'Phone', icon: '/images/yealinkt85.jpg' },
  { id: 8, name: 'YEALINK T87', category: 'Phone', icon: '/images/T87W.jpg' },
  { id: 9, name: 'YEALINK T88', category: 'Phone', icon: '/images/Yealink_new.jpg' },
  { id: 10, name: 'Grandstream 3470', category: 'Phone', icon: '/images/Grandstream_3470.jpg' },
  { id: 11, name: 'Grandstream 3480', category: 'Phone', icon: '/images/3480.jpg' },
  { id: 12, name: 'Cisco 9871', category: 'Phone', icon: '/images/CISCO.jpeg' },
  { id: 13, name: 'Yealink W73H DECT', category: 'Phone', icon: '/images/ipdect.jpg' },
  { id: 14, name: 'IP DECT Base', category: 'Phone', icon: '/images/ipdect.jpg' },
  { id: 15, name: 'Wi-Fi Booster', category: 'Network', icon: '/images/wifibooster.jpg' },
  { id: 16, name: 'Headsets', category: 'Audio', icon: '/images/headset.jpg' },
  { id: 17, name: 'Fibre TV/Wallboard', category: 'Display', icon: '/images/fibretv.jpg' },
  { id: 18, name: 'iPad', category: 'Tablet', icon: '/images/ipad.png' },
  { id: 19, name: 'Fibre Audio', category: 'Audio', icon: '🎧' },
  { id: 20, name: 'Add On', category: 'Display', icon: '/images/fibretv.jpg' },
];

const getDefaultAdditionalHardware = () => [
  { id: 'fibre', name: 'Fibre', category: 'Music Module', icon: '/images/music.svg' },
  { id: 'media-player', name: 'Media Player', category: 'Music Module', icon: '/images/module.svg' },
  { id: 'aa-voice-over', name: 'AA Voice Over', category: 'Music Module', icon: '/images/music.svg' },
  { id: 'site-link-licence', name: 'Site Link Licence', category: 'Site Link Licence', icon: '/images/sitelink.png' },
  { id: 'analogue-devices', name: 'Analogue Devices', category: 'Site Link Licence', icon: '/images/sitelink.png' },
  { id: 'ups', name: 'UPS', category: 'Protection', icon: '/images/protection.svg' },
  { id: 'surge-protection', name: 'Surge protection', category: 'Protection', icon: '/images/protection.svg' },
  { id: 'pancode', name: 'Pancode', category: 'Door entry', icon: '/images/doorentry.png' },
  { id: '1-button-entry', name: '1 Button Entry', category: 'Door entry', icon: '/images/doorentry.png' },
  { id: 'ucm-ip-pbx', name: 'UCM IP PBX', category: 'UCM IP PBX', icon: '/images/ucmippbx.jpg' },
  { id: 'voicemail-ports', name: 'Voicemail ports', category: 'UCM IP PBX', icon: '/images/ucmippbx.jpg' },
  { id: 'wifi-enabled', name: 'Draytek 2865LAC', category: 'Router', icon: '/images/router.png' },
  { id: '8-port-poe', name: 'Netgear GS108LP', category: 'POE', icon: '/images/poe.png' },
  { id: '24-port-poe', name: '24 port POE', category: 'POE', icon: '/images/poe.png' },
  { id: 'cat5e-cable-5m', name: 'Cat 5E Cable (5m)', category: 'Cabling', icon: '/images/cat5e.jpg' },
  { id: 'system-moves', name: 'System Moves', category: 'System Moves', icon: '/images/systemmoves.png' },
  { id: 'cat5', name: 'Cat 5', category: 'System Moves', icon: '/images/systemmoves.png' },
];

const LICENCE_SERVICES = [
  { key: 'Wizzyl', label: 'Wizzyl', description: 'Website Platform', monthlyPrice: 49.99 },
  { key: '6ELA', label: '6ELA', description: 'Invoice & Credit Control Platform', monthlyPrice: 49.99 },
];

const MOBILE_PHONE_PRICES = {
  director_premium: 1400,
  director: 1000,
  manager: 750,
  standard: 400,
};

const LEASE_RATES = { 84: 17.2, 72: 19, 60: 23, 48: 27, 36: 36 };

const fmt = (v) => `£${Number(v || 0).toFixed(2)}`;

/* ──────────────────────────────────────────────────────────────────────── */

const InfoRow = ({ label, value, focQty, canSeeFoc }) => (
  <div className="p-4 hover:bg-blue-50 transition-colors duration-200 border-b border-blue-100 last:border-b-0">
    <div className="flex justify-between items-center">
      <span className="text-gray-700 font-medium">
        {label}
        {canSeeFoc && focQty > 0 && (
          <span className="ml-1 text-xs text-emerald-600 font-normal">
            ({value} + {focQty} FOC)
          </span>
        )}
      </span>
      <span className="w-20 px-3 py-2 text-center font-semibold text-blue-700">
        {(value || 0) + (canSeeFoc ? (focQty || 0) : 0)}
      </span>
    </div>
  </div>
);

const MoneyRow = ({ label, value, highlight, badge }) => (
  <div className={`flex justify-between px-3 py-2 border-b border-gray-200 last:border-b-0 ${highlight ? 'bg-green-50' : ''}`}>
    <span className="flex items-center">
      {label}
      {badge && (
        <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">{badge}</span>
      )}
    </span>
    <span className={highlight ? 'font-bold text-green-700' : ''}>{fmt(value)}</span>
  </div>
);

const getIconComponent = (category) => {
  switch (category) {
    case 'Phone': return <Phone className="w-5 h-5" />;
    case 'Audio': return <Headphones className="w-5 h-5" />;
    case 'Network': return <Wifi className="w-5 h-5" />;
    case 'Display': return <Monitor className="w-5 h-5" />;
    case 'Tablet': return <Tablet className="w-5 h-5" />;
    case 'Software': return <Smartphone className="w-5 h-5" />;
    default: return <Monitor className="w-5 h-5" />;
  }
};

// Read-only hardware card with proper image fallback via the default item list.
const HardwareCard = ({ item, canSeeFoc }) => (
  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
    <div className="flex items-center space-x-3 mb-3">
      {(item.icon && typeof item.icon === 'string' && item.icon.startsWith('/images')) ? (
        <img
          src={item.icon}
          alt={item.name}
          className="w-12 h-12 object-contain rounded-md"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = '/images/placeholder.jpg';
            e.target.className = 'w-12 h-12 object-contain rounded-md bg-gray-100';
          }}
        />
      ) : (
        <span className="text-2xl">{(item.icon && typeof item.icon === 'string') ? item.icon : '⚙️'}</span>
      )}
      <div>
        <h5 className="font-semibold text-gray-800">{item.name}</h5>
        <p className="text-xs text-gray-600">{item.category}</p>
        {item.manuallySet && <span className="text-xs text-amber-600 font-normal">(manual)</span>}
      </div>
    </div>

    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-600">
        Quantity
        {canSeeFoc && (item.focQuantity || 0) > 0 && (
          <span className="ml-1 text-xs text-emerald-600 font-normal">
            ({item.quantity || 0} + {item.focQuantity} FOC)
          </span>
        )}
      </span>
      <span className="px-2 py-1 font-semibold text-blue-700">
        {(item.quantity || 0) + (canSeeFoc ? (item.focQuantity || 0) : 0)}
      </span>
    </div>

    {canSeeFoc && item.foc && (
      <div className="flex items-center pt-2 mt-2 border-t border-blue-100">
        <span className="text-xs font-semibold text-emerald-700">FOC applied</span>
      </div>
    )}
  </div>
);

const SectionHeader = ({ icon, title, count, badge, isOpen, onToggle }) => (
  <div
    className="bg-gradient-to-r from-blue-800 via-blue-900 to-black text-white rounded-lg p-6 cursor-pointer flex justify-between items-center hover:from-gray-700 hover:via-gray-800 hover:to-gray-900 transition-all duration-300"
    onClick={onToggle}
  >
    <div className="flex items-center space-x-3">
      {icon}
      <h2 className="text-xl font-bold tracking-wide">{title}</h2>
      {typeof count === 'number' && (
        <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">
          {count} items
        </span>
      )}
    </div>
    <div className="flex items-center space-x-2">
      {!isOpen && badge && (
        <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">{badge}</span>
      )}
      <span className="text-blue-200 text-sm font-medium">{isOpen ? 'Collapse' : 'Expand'}</span>
      <div className="bg-blue-500 rounded-full p-1">
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>
    </div>
  </div>
);

const SystemConfigView = ({ selectedConnectionId }) => {
  const [configData, setConfigData] = useState(null);
  const [rentalData, setRentalData] = useState(null);
  const [mobileData, setMobileData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [itSupportPricing, setItSupportPricing] = useState([]);

  const [isLineServicesOpen, setIsLineServicesOpen] = useState(true);
  const [isHardwareOpen, setIsHardwareOpen] = useState(false);
  const [isServiceChargesOpen, setIsServiceChargesOpen] = useState(false);
  const [isDigitalOpen, setIsDigitalOpen] = useState(false);
  const [isItServicesOpen, setIsItServicesOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCostComparisonOpen, setIsCostComparisonOpen] = useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);

  const userRole = localStorage.getItem('Role');
  const canSeeFoc = userRole !== 'Team Member';
  const isAdmin = userRole === 'Admin' || userRole === 'Support';

  useEffect(() => {
    if (!selectedConnectionId) return;

    const fetchAll = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getSystemConfig(selectedConnectionId);
        if (result && result.data && Object.keys(result.data).length > 0) {
          setConfigData(result.data);
        } else {
          setConfigData(null);
        }
      } catch (err) {
        console.error('Failed to load system config:', err);
        setError('Failed to load system configuration');
      } finally {
        setIsLoading(false);
      }

      // Best-effort — these two are read-only lookups that shouldn't block
      // the main config view if the connection has no rental/mobile record.
      try {
        const rentalResult = await getRentalProfiling(selectedConnectionId);
        if (rentalResult && rentalResult.status === 'success' && rentalResult.data) {
          setRentalData(rentalResult.data);
        }
      } catch (err) {
        console.error('Failed to load rental profiling:', err);
      }

      try {
        const mobileResult = await MobileService.getMobileService(selectedConnectionId);
        setMobileData(mobileResult);
      } catch (err) {
        console.error('Failed to load mobile service data:', err);
      }

       try {
          const mechanicsResult = await fetchMechanicsData();
          setLicenses(mechanicsResult?.data?.licenses || []);
          setItSupportPricing(mechanicsResult?.data?.it_support || []);
        } catch (err) {
          console.error('Failed to load mechanics pricing data:', err);
        }
    };

    fetchAll();
  }, [selectedConnectionId]);

  // ── Derived, hardware-image-safe lists ──
  const mergedHardwareItems = useMemo(() => {
    const items = configData?.hardwareItems;
    if (!Array.isArray(items)) return [];
    const defaults = getDefaultHardwareItems();
    return items.map(saved => {
      const def = defaults.find(d => d.name.trim().toLowerCase() === (saved.name || '').trim().toLowerCase());
      return {
        ...def,
        ...saved,
        icon: saved.icon || def?.icon || '⚙️',
        category: saved.category || def?.category || 'Other',
      };
    });
  }, [configData]);

  const mergedAdditionalHardware = useMemo(() => {
    const items = configData?.additionalHardware;
    if (!Array.isArray(items)) return [];
    const defaults = getDefaultAdditionalHardware();
    return items.map(saved => {
      const def = defaults.find(d => d.name.trim().toLowerCase() === (saved.name || '').trim().toLowerCase());
      return {
        ...def,
        ...saved,
        icon: saved.icon || def?.icon || '⚙️',
        category: saved.category || def?.category || 'Other',
      };
    });
  }, [configData]);

  // ── Derived cost figures ──
  const rentalCostsTotal = useMemo(() => {
    if (!rentalData) return 0;
    return (
      (parseFloat(rentalData.cashback_visible) || 0) +
      (parseFloat(rentalData.lines_termination) || 0) +
      (parseFloat(rentalData.calls_termination) || 0) +
      (parseFloat(rentalData.current_lease_buy_outs) || 0) +
      (parseFloat(rentalData.mobile_buyouts) || 0) +
      (parseFloat(rentalData.other_costs) || 0)
    );
  }, [rentalData]);

 const currentMobileCostLive = useMemo(() => {
    if (!mobileData?.staff_levels) return null;
    return mobileData.staff_levels.reduce((total, phone) => {
      const rate = MOBILE_PHONE_PRICES[phone.staff_level?.toLowerCase()] || 0;
      const count = parseInt(phone.count) || 0;
      return total + rate * count;
    }, 0);
  }, [mobileData]);

  const itServicesBreakdown = useMemo(() => {
    const itServicesLive = configData?.itServices || {};

    const getItSupportPrice = (name) => {
      const item = itSupportPricing.find(i => i.name === name);
      return item ? parseFloat(item.price) || 0 : 0;
    };
    const getLicencePrice = (name) => {
      const item = licenses.find(l => l.name === name);
      return item ? parseFloat(item.price) || 0 : 0;
    };
    const roundToTwo = (num) => parseFloat((Math.round(num * 100) / 100).toFixed(2));

    const stations = parseInt(itServicesLive.numberOfStations) || 0;
    const licenceQuantities = itServicesLive.licenceQuantities || {};

    const licenceBreakdown = licenses
      .map(license => {
        const qty = parseInt(licenceQuantities[license.id]) || 0;
        const price = parseFloat(license.price) || 0;
        return { id: license.id, name: license.name, price, quantity: qty, lineTotal: roundToTwo(qty * price) };
      })
      .filter(l => l.quantity > 0);

    const licenceCost = roundToTwo(licenceBreakdown.reduce((sum, l) => sum + l.lineTotal, 0));

    const basePerStationPrice = getItSupportPrice('PER STATION + MICROSOFT LICENCE BASIC');
    const stationPackages = {
      it_support: { label: 'IT Support only', price: getItSupportPrice('IT SUPPORT (PER STATION)') },
      basic: { label: '+ Microsoft Business Basic', price: basePerStationPrice },
      standard: { label: '+ Microsoft Business Standard', price: roundToTwo(basePerStationPrice + getLicencePrice('MICROSOFT BUSINESS STANDARD')) },
      premium: { label: '+ Microsoft Business Premium', price: roundToTwo(basePerStationPrice + getLicencePrice('MICROSOFT BUSINESS PREMIUM')) },
    };
    const selectedKeys = itServicesLive.selectedPackages || [];
    const selectedPackageLabels = selectedKeys.map(k => stationPackages[k]?.label).filter(Boolean);
    const perStationPrice = roundToTwo(selectedKeys.reduce((sum, key) => sum + (stationPackages[key]?.price || 0), 0));
    const stationCost = roundToTwo(stations * perStationPrice);

    const itServiceCost = roundToTwo(getItSupportPrice('IT SERVICE'));

    return { stations, licenceBreakdown, licenceCost, selectedPackageLabels, perStationPrice, stationCost, itServiceCost };
  }, [configData, licenses, itSupportPricing]);

  if (isLoading) {
    return (
      <div className="w-full max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-blue-100 p-8">
        <div className="flex items-center justify-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-blue-600 font-medium">Loading configuration...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-red-100 p-8">
        <div className="flex items-center justify-center space-x-3 text-red-600">
          <AlertCircle className="w-8 h-8" />
          <span className="font-medium">{error}</span>
        </div>
      </div>
    );
  }

  if (!configData) {
    return (
      <div className="w-full max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-blue-100 p-8">
        <div className="flex items-center justify-center space-x-3 text-gray-500">
          <Settings className="w-8 h-8" />
          <span className="font-medium">No system configuration has been saved for this connection yet.</span>
        </div>
      </div>
    );
  }

  const {
    lineRequirements = {},
    currentLines = {},
    dataBroadband = {},
    linesFoc = {},
    serviceCharges = {},
    leaseCalculatorData = {},
    telecom_details = [],
    notes = '',
    totalMonthlyCost,
    totalEquipmentCost,
  } = configData;

  const digitalServices = leaseCalculatorData?.digital_services || {};
  const licenceQtys = digitalServices.licenceServices || {};
  const axoPackage = digitalServices.axoSignalPackage || null;
  const webHostingCost = digitalServices.webHostingCost || 0;
  const totalNewDigitalCost = digitalServices.totalNewDigitalCost || 0;

  const itServices = configData.itServices || {};
  const itServicesTotalSaved = Number(
    configData.itServicesTotal ?? leaseCalculatorData.it_services_total ?? 0
  ) || 0;

  // ── Cost Comparison figures (all sourced from what was actually saved) ──
  const savedActualLeasePayment = Number(leaseCalculatorData.actualLeasePayment) || 0;
  const savedActualProfit = Number(leaseCalculatorData.actualProfit) || 0;
  const totalCostsToUs = savedActualLeasePayment - savedActualProfit;
  const actualProfitPercentage = totalCostsToUs > 0 ? (savedActualProfit / totalCostsToUs) * 100 : 0;
  const salesRepUnits = (Number(leaseCalculatorData.salesRepCommission) || 0) / 1000;

  const paymentProfile = leaseCalculatorData.paymentProfile ?? 84;
  const leaseRate = LEASE_RATES[paymentProfile] || 17.2;
  const discountLoad = Number(leaseCalculatorData.discountLoad) || 0;
  const baseMonthlyRental = (Number(leaseCalculatorData.adjustedRental) || 0) - discountLoad;
  const requiredLeasePayment = leaseRate > 0 ? (baseMonthlyRental / leaseRate) * 1000 : 0;
  const desiredProfit = requiredLeasePayment - totalCostsToUs;

  const currentTotalPackageCost = (Number(leaseCalculatorData.current_total_cost) || 0) + webHostingCost;

  const currentChargesItems = [
    { label: 'Current Phone System Lease Rental', value: leaseCalculatorData.current_lease_rental },
    { label: 'Current Mobile Phone Charges', value: leaseCalculatorData.current_mobile_charges },
    { label: 'Current Call Charges', value: leaseCalculatorData.currentCallCharges },
    { label: 'Current Service Charges, including Line rental, diverts etc', value: leaseCalculatorData.current_service_charges },
    { label: 'Current Maintenance', value: leaseCalculatorData.current_maintenance },
    { label: 'Other Charges', value: leaseCalculatorData.other_charges },
    { label: 'Current Digital Services Cost', value: webHostingCost },
  ];

  const newMonthlyChargesItems = [
    { label: 'Telephone System Lease Rental', value: leaseCalculatorData.adjustedRental, highlight: true, badge: 'Auto-calculated' },
    { label: 'Estimated Mobile Charges', value: leaseCalculatorData.estimated_mobile_charges },
    { label: 'Estimated Call Charges', value: leaseCalculatorData.estimatedCallCharges },
    {
      label: (currentLines.hostedLifetimeLicenses || 0) > 0
        ? 'New service charges, inclusive local, national and mobile calls for full term'
        : 'New Service charges',
      value: leaseCalculatorData.new_service_charges ?? totalMonthlyCost,
    },
    { label: 'Network Settlement Contribution - Paid in lump sum (VAT EXEMPT) figure showing per month pro rata', value: leaseCalculatorData.networkSettlementContribution },
    { label: 'Leased Line', value: leaseCalculatorData.leased_line },
    { label: 'Digital Service Charges', value: leaseCalculatorData.digital_service_charges ?? totalNewDigitalCost },
    { label: 'IT Service Charge', value: leaseCalculatorData.it_service_charge },
    { label: 'IT Services / Microsoft Licences', value: itServicesTotalSaved },
  ];
  const newTotalPackageCost = newMonthlyChargesItems.reduce((sum, i) => sum + (Number(i.value) || 0), 0);

  return (
    <div className="w-full max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-blue-100">
      {/* Read-only banner */}
      <div className="px-6 py-4 flex items-center space-x-3 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
        <Settings className="w-5 h-5 text-gray-500" />
        <div>
          <p className="text-sm font-semibold text-gray-800">System Configuration</p>
          <p className="text-xs text-gray-500 flex items-center space-x-1">
            <CheckCircle className="w-3 h-3" />
            <span>Read-only view</span>
          </p>
        </div>
      </div>

      {/* Line services + line requirements */}
      <SectionHeader
        icon={<Settings className="w-6 h-6" />}
        title="LINE SERVICES"
        isOpen={isLineServicesOpen}
        onToggle={() => setIsLineServicesOpen(!isLineServicesOpen)}
      />
      {isLineServicesOpen && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50">
          {telecom_details.length > 0 && (
            <div className="p-6">
              <div className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden max-w-4xl mx-auto">
                <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 border-b border-blue-200">
                  <h4 className="font-semibold text-blue-800 text-center">Telecom Services</h4>
                </div>
                <div className="divide-y divide-blue-100">
                  {telecom_details.map((service, i) => (
                    <div key={i} className="p-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div><span className="text-gray-500 text-xs block">Description</span>{service.description || '—'}</div>
                      <div><span className="text-gray-500 text-xs block">Type</span>{service.service_type || '—'}</div>
                      <div><span className="text-gray-500 text-xs block">Existing Tel No</span>{service.existing_tel_no || '—'}</div>
                      <div><span className="text-gray-500 text-xs block">Qty</span>{service.quantity || '—'}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 shadow-md">
            <div className="flex items-center justify-center space-x-2">
              <Wifi className="w-5 h-5" />
              <h3 className="font-bold text-lg">LINE REQUIREMENTS</h3>
            </div>
          </div>

          <div className="p-6">
            <div className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden max-w-3xl mx-auto">
              <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 border-b border-blue-200">
                <h4 className="font-semibold text-blue-800 text-center">New Lines &amp; Internet</h4>
              </div>
              <InfoRow label="Hosted Lifetime Licenses with SIP Trunk" value={currentLines.hostedLifetimeLicenses} focQty={linesFoc.hostedLifetimeLicensesFocQty} canSeeFoc={canSeeFoc} />
              <InfoRow label="New 4G / 5G Broadband" value={currentLines.new4g5gBroadband} focQty={linesFoc.new4g5gBroadbandFocQty} canSeeFoc={canSeeFoc} />
              <InfoRow label="FTTP / SoGEA Broadband" value={currentLines.fttpSogeaBroadband} focQty={linesFoc.fttpSogeaBroadbandFocQty} canSeeFoc={canSeeFoc} />
              <InfoRow label="Starlink Broadband" value={dataBroadband.starlinkBroadband} focQty={linesFoc.starlinkBroadbandFocQty} canSeeFoc={canSeeFoc} />
              <InfoRow label="Leased Lines" value={dataBroadband.leasedLines} focQty={linesFoc.leasedLinesFocQty} canSeeFoc={canSeeFoc} />
              <InfoRow label="Analogue PSTN Lines" value={currentLines.analoguePstnLines} canSeeFoc={false} />
              <InfoRow label="Existing ISDN2 Channels" value={lineRequirements.existingIsdn2Channels} canSeeFoc={false} />
              <InfoRow label="New Conversion ISDN2 Channel" value={lineRequirements.newConversionIsdn2Channel} canSeeFoc={false} />
              <InfoRow label="Existing ISDN30 Channels" value={lineRequirements.existingIsdn30Channels} canSeeFoc={false} />
              <InfoRow label="New ISDN30 Channel" value={lineRequirements.newIsdn30Channel} canSeeFoc={false} />
            </div>
          </div>
        </div>
      )}

      {/* Hardware */}
      <div className="my-[20px]">
        <SectionHeader
          icon={<Monitor className="w-6 h-6" />}
          title="HARDWARE"
          count={mergedHardwareItems.length}
          isOpen={isHardwareOpen}
          onToggle={() => setIsHardwareOpen(!isHardwareOpen)}
        />
      </div>
      {isHardwareOpen && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50">
          <div className="p-6 space-y-6">
            {Object.entries(
              mergedHardwareItems.reduce((acc, item) => {
                (acc[item.category] = acc[item.category] || []).push(item);
                return acc;
              }, {})
            ).map(([category, items]) => (
              <div key={category} className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 border-b border-blue-200">
                  <div className="flex items-center space-x-2">
                    {getIconComponent(category)}
                    <h4 className="font-bold text-blue-800">{category}</h4>
                    <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium">{items.length}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                  {items.map(item => (
                    <HardwareCard key={item.id || item.name} item={item} canSeeFoc={canSeeFoc} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="p-6 space-y-6">
            {Object.entries(
              mergedAdditionalHardware.reduce((acc, item) => {
                (acc[item.category] = acc[item.category] || []).push(item);
                return acc;
              }, {})
            ).map(([category, items]) => (
              <div key={category} className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 border-b border-blue-200">
                  <div className="flex items-center space-x-2">
                    {getIconComponent(category)}
                    <h4 className="font-bold text-blue-800">{category}</h4>
                    <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium">{items.length}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                  {items.map(item => (
                    <HardwareCard key={item.id || item.name} item={item} canSeeFoc={canSeeFoc} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Service Charges (from CallCostsComponent) */}
      <div className="my-[20px]">
        <SectionHeader
          icon={<PoundSterlingIcon className="w-6 h-6" />}
          title="CURRENT SERVICE CHARGE COSTS"
          isOpen={isServiceChargesOpen}
          onToggle={() => setIsServiceChargesOpen(!isServiceChargesOpen)}
        />
      </div>
      {isServiceChargesOpen && (
        <div className="bg-gray-50 p-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Service Charge Breakdown</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { field: 'service_charge', label: 'Existing Service Charges' },
                { field: 'equipment_rentals', label: 'Existing Equipment Rentals' },
                { field: 'media', label: 'PH Media / On Hold Marketing' },
                { field: 'maintenance', label: 'Existing Maintenance' },
                { field: 'broadband', label: 'Existing Broadband Charges' },
                { field: 'utilities', label: 'Any Other Costs' },
              ].map(({ field, label }) => (
                <div key={field} className="flex justify-between items-center px-3 py-2 border border-gray-200 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <span className="font-semibold text-gray-800">{fmt(serviceCharges[field])}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center">
              <span className="text-lg font-bold text-gray-800">Total Service Charges:</span>
              <span className="text-xl font-bold text-blue-600">
                {fmt(Object.values(serviceCharges).reduce((a, b) => a + (Number(b) || 0), 0))}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Website Hosting & Digital Services */}
      <div className="my-[20px]">
        <SectionHeader
          icon={<Globe className="w-6 h-6" />}
          title="WEBSITE HOSTING & DIGITAL SERVICES"
          badge={(webHostingCost > 0 || totalNewDigitalCost > 0)
            ? `Current hosting: ${fmt(webHostingCost)} | New services: ${fmt(totalNewDigitalCost)}`
            : null}
          isOpen={isDigitalOpen}
          onToggle={() => setIsDigitalOpen(!isDigitalOpen)}
        />
      </div>
      {isDigitalOpen && (
        <div className="bg-gray-50 p-6 space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
              <Monitor className="w-5 h-5 text-blue-600" /> Current Spend
            </h3>
            <p className="text-sm text-gray-500 mb-2">Current website hosting spend on record.</p>
            <p className="text-2xl font-bold text-gray-800">{fmt(webHostingCost)}<span className="text-sm font-normal text-gray-500">/mo</span></p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" /> Wizzyl &amp; 6ELA — Licence Subscriptions
            </h3>
            {LICENCE_SERVICES.every(svc => !(licenceQtys[svc.key] > 0)) ? (
              <p className="text-sm text-gray-400 italic">No licences selected.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {LICENCE_SERVICES.filter(svc => licenceQtys[svc.key] > 0).map(svc => {
                  const qty = licenceQtys[svc.key] || 0;
                  return (
                    <div key={svc.key} className="rounded-xl border-2 border-blue-500 bg-blue-50 p-5">
                      <span className="inline-block bg-gradient-to-r from-blue-800 to-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full mb-1">{svc.label}</span>
                      <p className="text-xs text-gray-500 mt-0.5">{svc.description}</p>
                      <div className="mt-3 flex justify-between text-sm">
                        <span className="text-gray-600">{qty} licence{qty !== 1 ? 's' : ''}</span>
                        <span className="font-semibold text-gray-700">{fmt(qty * svc.monthlyPrice)}/mo</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-blue-600" /> AXO Signal — Market Intelligence
            </h3>
            {axoPackage ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Selected package</p>
                  <p className="text-sm font-bold text-blue-900">{axoPackage.label || axoPackage.key}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Customer pays</p>
                  <p className="text-sm font-bold text-gray-800">{fmt(axoPackage.price)}/mo</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No AXO Signal package selected.</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
              <span className="text-xs text-gray-500 mb-1">Current Hosting Cost</span>
              <span className="text-lg font-bold text-gray-800">{fmt(webHostingCost)}<span className="text-xs font-normal text-gray-500">/mo</span></span>
            </div>
            <div className="flex flex-col bg-blue-50 rounded-lg px-4 py-3 border border-blue-200">
              <span className="text-xs text-gray-500 mb-1">New Digital Services Cost</span>
              <span className="text-lg font-bold text-blue-700">{fmt(totalNewDigitalCost)}<span className="text-xs font-normal text-blue-500">/mo</span></span>
            </div>
          </div>
        </div>
      )}

      {/* Current Mobile Costs */}
      <div className="my-[20px]">
        <SectionHeader
          icon={<PoundSterlingIcon className="w-6 h-6" />}
          title="CURRENT MOBILE COSTS"
          isOpen={isMobileOpen}
          onToggle={() => setIsMobileOpen(!isMobileOpen)}
        />
      </div>
       <div className="my-[20px]">
        <SectionHeader
          icon={<Globe className="w-6 h-6" />}
          title="IT SERVICES & MICROSOFT LICENCES"
          badge={itServicesTotalSaved > 0 ? `${fmt(itServicesTotalSaved)}/mo` : null}
          isOpen={isItServicesOpen}
          onToggle={() => setIsItServicesOpen(!isItServicesOpen)}
        />
      </div>
      {isItServicesOpen && (
        <div className="bg-gray-50 p-6 space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" /> Stations &amp; Package
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
                <span className="text-xs text-gray-500 mb-1">Number of Stations</span>
                <span className="text-lg font-bold text-gray-800">{itServicesBreakdown.stations}</span>
              </div>
              <div className="flex flex-col bg-blue-50 rounded-lg px-4 py-3 border border-blue-200">
                <span className="text-xs text-gray-500 mb-1">Station Cost</span>
                <span className="text-lg font-bold text-blue-700">
                  {fmt(itServicesBreakdown.stationCost)}<span className="text-xs font-normal text-blue-500">/mo</span>
                </span>
              </div>
            </div>
            {itServicesBreakdown.selectedPackageLabels.length > 0 ? (
              <div className="mt-4">
                <span className="text-xs text-gray-500 block mb-2">Selected packages ({fmt(itServicesBreakdown.perStationPrice)} / station)</span>
                <div className="flex flex-wrap gap-2">
                  {itServicesBreakdown.selectedPackageLabels.map((label, i) => (
                    <span key={i} className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">{label}</span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic mt-4">No station package selected.</p>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" /> Microsoft 365 Licences
            </h3>
            {itServicesBreakdown.licenceBreakdown.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No Microsoft 365 licences selected.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {itServicesBreakdown.licenceBreakdown.map(l => (
                  <div key={l.id} className="flex justify-between py-2 text-sm">
                    <span className="text-gray-700">{l.name} ({l.quantity} × {fmt(l.price)})</span>
                    <span className="font-semibold text-gray-800">{fmt(l.lineTotal)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex flex-col bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
              <span className="text-xs text-gray-500 mb-1">Licence Cost</span>
              <span className="text-lg font-bold text-gray-800">{fmt(itServicesBreakdown.licenceCost)}<span className="text-xs font-normal text-gray-500">/mo</span></span>
            </div>
            <div className="flex flex-col bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
              <span className="text-xs text-gray-500 mb-1">IT Service Charge</span>
              <span className="text-lg font-bold text-gray-800">{fmt(itServicesBreakdown.itServiceCost)}<span className="text-xs font-normal text-gray-500">/mo</span></span>
            </div>
            <div className="flex flex-col bg-blue-50 rounded-lg px-4 py-3 border border-blue-200">
              <span className="text-xs text-gray-500 mb-1">Total</span>
              <span className="text-lg font-bold text-blue-700">{fmt(itServicesTotalSaved)}<span className="text-xs font-normal text-blue-500">/mo</span></span>
            </div>
          </div>
        </div>
      )}
      {isMobileOpen && (
        <div className="bg-gray-50 p-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {!mobileData ? (
              <p className="text-sm text-gray-400 italic">No mobile service data recorded for this connection.</p>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-800">Mobile Cost Breakdown</h3>
                  <span className="text-sm text-gray-500">
                    Existing costs: <span className="font-semibold text-gray-800">{fmt(mobileData.existing_costs)}</span>
                  </span>
                </div>

                {Array.isArray(mobileData.staff_levels) && mobileData.staff_levels.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">By Staff Level</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {mobileData.staff_levels.map((phone, i) => {
                        const rate = MOBILE_PHONE_PRICES[phone.staff_level?.toLowerCase()] || 0;
                        const count = parseInt(phone.count) || 0;
                        return (
                          <div key={i} className="flex justify-between px-3 py-2 border border-gray-200 rounded-lg">
                            <span className="text-gray-600">{phone.staff_level} ({count} @ {fmt(rate)})</span>
                            <span className="font-medium">{fmt(rate * count)}</span>
                          </div>
                        );
                      })}
                    </div>
                    {currentMobileCostLive !== null && (
                      <div className="mt-3 flex justify-between border-t border-gray-200 pt-3">
                        <span className="font-semibold text-gray-700">Total (by staff level)</span>
                        <span className="font-bold text-gray-800">{fmt(currentMobileCostLive)}</span>
                      </div>
                    )}
                  </div>
                )}

                {Array.isArray(mobileData.data_plans) && mobileData.data_plans.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Data Plans</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {mobileData.data_plans.map((plan, i) => (
                        <div key={i} className="flex justify-between px-3 py-2 border border-gray-200 rounded-lg">
                          <span className="text-gray-600">{plan.data_allowance} × {plan.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Cost Comparison & Lease Calculator */}
      <div className="my-[20px]">
        <SectionHeader
          icon={<PoundSterlingIcon className="w-6 h-6" />}
          title="COST COMPARISON & LEASE CALCULATOR"
          isOpen={isCostComparisonOpen}
          onToggle={() => setIsCostComparisonOpen(!isCostComparisonOpen)}
        />
      </div>
      {isCostComparisonOpen && (
        <div className="p-6 bg-white border border-gray-300 rounded-md m-6 mt-0">
          {!rentalData && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md text-center text-sm text-gray-600">
              No rental profile has been configured for this connection.
            </div>
          )}

          {/* Session summary */}
          <h2 className="text-lg font-bold mb-4 text-center text-gray-800">SESSION SUMMARY</h2>
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {isAdmin && (
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-600 mb-2">Total Equipment Cost</h3>
                  <div className="text-2xl font-bold text-blue-600">{fmt(totalEquipmentCost)}</div>
                </div>
              )}
              {isAdmin && (
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-600 mb-2">Current Mobile Cost</h3>
                  <div className="text-2xl font-bold text-green-600">
                    {fmt(currentMobileCostLive ?? leaseCalculatorData.current_mobile_charges)}
                  </div>
                </div>
              )}
              <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">Total Additional Costs</h3>
                <div className="text-2xl font-bold text-orange-600">{fmt(rentalCostsTotal)}</div>
                <div className="text-xs text-gray-500 mt-1">Cashback + Termination + Buyouts</div>
              </div>
            </div>

            {isAdmin && rentalData && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Additional Costs Breakdown:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between"><span className="text-gray-600">Cashback visible to customer:</span><span className="font-medium">{fmt(rentalData.cashback_visible)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Lines Termination Charges:</span><span className="font-medium">{fmt(rentalData.lines_termination)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Calls Termination Charges:</span><span className="font-medium">{fmt(rentalData.calls_termination)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Current Lease/Rental Buy Outs:</span><span className="font-medium">{fmt(rentalData.current_lease_buy_outs)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Mobile Buyouts:</span><span className="font-medium">{fmt(rentalData.mobile_buyouts)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Other Costs:</span><span className="font-medium">{fmt(rentalData.other_costs)}</span></div>
                </div>
              </div>
            )}

            {isAdmin && (
              <div className="mt-6 pt-4 border-t-2 border-gray-300 flex justify-between items-center">
                <span className="text-lg font-bold text-gray-800">Total Costs to Us:</span>
                <span className="text-2xl font-bold text-indigo-600">{fmt(totalCostsToUs)}</span>
              </div>
            )}
          </div>

          {/* Lease calculator results */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 mb-8">
            <div className="flex items-center mb-4">
              <Calculator className="w-6 h-6 text-green-600 mr-2" />
              <h3 className="text-lg font-bold text-gray-800">Lease Calculator</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-sm">
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <span className="text-gray-500 text-xs block">Payment Profile</span>
                <span className="font-semibold text-gray-800">{paymentProfile} months</span>
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <span className="text-gray-500 text-xs block">Network Settlement</span>
                <span className="font-semibold text-gray-800">{leaseCalculatorData.networkPayment ?? '—'} months</span>
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <span className="text-gray-500 text-xs block">Discount / Load</span>
                <span className="font-semibold text-gray-800">{discountLoad >= 0 ? '+' : ''}{fmt(discountLoad)}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {isAdmin && (
                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <h4 className="text-sm font-semibold text-gray-600 mb-2">Required Monthly Rental</h4>
                  <div className="text-xl font-bold text-blue-600">{fmt(leaseCalculatorData.adjustedRental)}</div>
                  <div className="text-xs text-gray-500 mt-1">What to quote customer</div>
                </div>
              )}
              
             
              <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                <h4 className="text-sm font-semibold text-gray-600 mb-2">Sales Rep Commission</h4>
                <div className="text-xl font-bold text-orange-600">{salesRepUnits.toFixed(2)} units</div>
              </div>
            </div>

           
          </div>

          {/* Current provider mobiles */}
          <div className="mb-8">
            <h2 className="text-lg font-bold mb-2">Current Provider Mobiles (Excl VAT)</h2>
            <div className="border border-gray-300 rounded-md overflow-hidden">
              {currentChargesItems.map((item, i) => (
                <MoneyRow key={i} label={item.label} value={item.value} />
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
              <span className="text-lg font-bold text-gray-800">Current Total Package Cost:</span>
              <span className="text-xl font-bold text-blue-600">{fmt(currentTotalPackageCost)}</span>
            </div>
          </div>

          {/* New monthly charges */}
          <div>
            <h2 className="text-lg font-bold mb-2">New Monthly Charges (Excl VAT)</h2>
            <div className="border border-gray-300 rounded-md overflow-hidden">
              {newMonthlyChargesItems.map((item, i) => (
                <MoneyRow key={i} label={item.label} value={item.value} highlight={item.highlight} badge={item.badge} />
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
              <span className="text-lg font-bold text-gray-800">New Total Package Cost:</span>
              <span className="text-xl font-bold text-blue-600">{fmt(newTotalPackageCost)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      <div className="my-[20px]">
        <SectionHeader
          icon={<Edit2 className="w-6 h-6" />}
          title="NOTES"
          isOpen={isNotesOpen}
          onToggle={() => setIsNotesOpen(!isNotesOpen)}
        />
      </div>
      {isNotesOpen && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
          <div className="bg-white rounded-xl shadow-lg border border-blue-100 overflow-hidden max-w-3xl mx-auto">
            <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 border-b border-blue-200">
              <h4 className="font-semibold text-blue-800 text-center">Internal Notes</h4>
            </div>
            <div className="p-4">
              {notes.trim() ? (
                <p className="whitespace-pre-wrap text-gray-700 text-sm leading-relaxed">{notes}</p>
              ) : (
                <p className="text-gray-400 text-sm italic">No notes recorded.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemConfigView;